// src/pages/Admin-Dashboard/UserTable.jsx (or wherever it resides)
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DeleteUserModal from "./delete-user-modal"; // Assuming this handles its own button/modal logic
import ViewProfileModal from "./view-profile-modal";
import { toast } from "react-toastify"; // Using react-toastify for consistency with other files
import { FiEye, FiUser, FiChevronLeft, FiChevronRight, FiGrid, FiTrash2 } from "react-icons/fi"; // Added FiTrash2

export default function UserTable({
  users = [],
  loading,
  setUsers,
  onRowClick,
  onToggleStatus,
  onDeleteUser,
  pagination,
}) {
  const [toggleLoadingId, setToggleLoadingId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleToggle = async (userId, isActive) => {
    setToggleLoadingId(userId);
    try {
      // The onToggleStatus prop should handle the API call and toast notification
      await onToggleStatus(userId);
    } catch (error) {
      toast.error("Toggle failed");
    } finally {
      setToggleLoadingId(null);
    }
  };

  const getStatusColor = (isActive) => {
    return isActive
      ? "bg-green-100 text-green-700"
      : "bg-gray-200 text-gray-700";
  };
  
  // Helper to prevent event propagation from buttons inside a clickable row
  const stopPropagation = (e) => e.stopPropagation();

  return (
    <div className="mt-8 bg-white rounded-xl shadow-lg overflow-hidden">
      
      {/* Table Header Section */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <FiUser className="text-orange-500" />
          Staff User List ({users.length} shown)
        </h2>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      )}

      {/* Desktop Table */}
      {!loading && (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Email</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Role</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-orange-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr
                      key={user._id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => onRowClick(user._id)} // Optional row click handler
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 capitalize">{user.role}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(user.isActive)}`}>
                          {user.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        <div className="flex gap-3 justify-center items-center">
                          
                          {/* View Profile Button */}
                          <button
                            onClick={(e) => {
                              stopPropagation(e);
                              setSelectedUser(user);
                            }}
                            className="text-blue-600 hover:text-blue-900 flex items-center text-sm px-3 py-1 rounded-full transition hover:bg-blue-50"
                          >
                            <FiEye className="mr-1 h-4 w-4" /> View
                          </button>
                          
                          {/* Deactivate/Activate Button */}
                          <button
                            onClick={(e) => {
                              stopPropagation(e);
                              handleToggle(user._id, user.isActive);
                            }}
                            className={`text-sm px-3 py-1.5 rounded-full font-medium shadow-sm transition duration-200 ${
                              user.isActive
                                ? "bg-red-500 text-white hover:bg-red-600"
                                : "bg-green-500 text-white hover:bg-green-600" // Use green for activation
                            } ${toggleLoadingId === user._id ? "opacity-60 cursor-not-allowed" : ""}`}
                            disabled={toggleLoadingId === user._id}
                          >
                            {toggleLoadingId === user._id ? "..." : user.isActive ? "Deactivate" : "Activate"}
                          </button>

                          {/* 🟢 FIXED: The DeleteUserModal component is placed here. 
                             It must contain its own button/trigger logic internally,
                             and we use stopPropagation to prevent the row click. */}
                          <div onClick={stopPropagation}> 
                            <DeleteUserModal
                              userId={user._id}
                              setUsers={setUsers}
                              // onDelete is what updates the parent state (UserListPage) 
                              // and handles the local user list update/stats update.
                              onDelete={() => onDeleteUser(user._id)} 
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <FiGrid className="h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                        <p className="mt-1 text-sm text-gray-500">The user list is empty.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* NEW: Mobile Card View */}
          <div className="md:hidden p-4 space-y-4">
            {users.length > 0 ? (
              users.map((user) => (
                <div 
                  key={user._id} 
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => onRowClick(user._id)}
                >
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{user.firstName} {user.lastName}</h3>
                        <p className="text-sm text-gray-500">{user.role}</p>
                      </div>
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(user.isActive)}`}>
                        {user.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    
                    <dl className="mt-4 grid grid-cols-1 gap-x-4 gap-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Email</dt>
                        <dd className="mt-1 text-sm text-gray-900 truncate">{user.email}</dd>
                      </div>
                    </dl>
                    
                    <div className="mt-6 flex justify-around gap-2">
                      <button
                        onClick={(e) => {
                          stopPropagation(e);
                          setSelectedUser(user);
                        }}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <FiEye className="mr-2 h-4 w-4" /> View
                      </button>
                      
                      <button
                        onClick={(e) => {
                          stopPropagation(e);
                          handleToggle(user._id, user.isActive);
                        }}
                        className={`flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition duration-200 ${
                          user.isActive
                            ? "bg-red-500 hover:bg-red-600"
                            : "bg-green-500 hover:bg-green-600"
                        } ${toggleLoadingId === user._id ? "opacity-60 cursor-not-allowed" : ""}`}
                        disabled={toggleLoadingId === user._id}
                      >
                        {toggleLoadingId === user._id ? "..." : user.isActive ? "Deactivate" : "Activate"}
                      </button>
                      
                      {/* 🟢 FIXED: Delete action for mobile view */}
                      <div onClick={stopPropagation}>
                        {/* We'll use a simple button placeholder here, assuming DeleteUserModal will render a more complex button/modal */}
                         <DeleteUserModal
                            userId={user._id}
                            setUsers={setUsers}
                            onDelete={() => onDeleteUser(user._id)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <FiGrid className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                <p className="mt-1 text-sm text-gray-500">The user list is empty.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Pagination (Styled to match TimesheetTable) */}
      {pagination && pagination.totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiChevronLeft className="mr-2 h-5 w-5" />
              Previous
            </button>
            <button
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <FiChevronRight className="ml-2 h-5 w-5" />
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Page <span className="font-medium">{pagination.page}</span> of{" "}
                <span className="font-medium">{pagination.totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => pagination.onPageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <FiChevronLeft className="h-5 w-5" />
                </button>
                {/* Simplified page number rendering */}
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                    key={page}
                    onClick={() => pagination.onPageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pagination.page === page
                        ? "z-10 bg-orange-50 border-orange-500 text-orange-600"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                    >
                    {page}
                    </button>
                ))}
                <button
                  onClick={() => pagination.onPageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <FiChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modal for View Profile */}
      {selectedUser && (
        <ViewProfileModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
      
    </div>
  );
}